import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Users } from "lucide-react";
import ToolPageLayout from "@/components/tools/ToolPageLayout";
import { getRelatedTools } from "@/lib/toolsData";

const ChurnCalculator = () => {
  const [lostCustomers, setLostCustomers] = useState<string>("");
  const [startCustomers, setStartCustomers] = useState<string>("");
  const [result, setResult] = useState<number | null>(null);

  const calculate = () => {
    const lostNum = parseFloat(lostCustomers) || 0;
    const startNum = parseFloat(startCustomers) || 0;
    
    if (startNum === 0) {
      setResult(null);
      return;
    }

    const churn = (lostNum / startNum) * 100;
    setResult(churn);
  };

  const reset = () => {
    setLostCustomers("");
    setStartCustomers("");
    setResult(null);
  };

  const getAssessment = (rate: number) => {
    if (rate <= 2) return { label: "Excellent", color: "text-green-600" };
    if (rate <= 5) return { label: "Good", color: "text-green-500" };
    if (rate <= 7) return { label: "Average", color: "text-yellow-600" };
    if (rate <= 10) return { label: "Concerning", color: "text-orange-500" };
    return { label: "Critical", color: "text-red-600" };
  };

  const relatedTools = getRelatedTools("churn-calculator");

  return (
    <ToolPageLayout
      title="Churn Rate Calculator"
      description="Calculate your customer churn rate to measure retention health and identify growth opportunities."
      metaDescription="Churn rate measures the percentage of customers who stop doing business with you over a period. Reducing churn is often more profitable than acquiring new customers."
      icon={Users}
      slug="churn-calculator"
      formula={{
        equation: "Churn Rate = (Lost Customers / Starting Customers) × 100",
        variables: [
          { name: "Lost Customers", description: "Customers who left during the period" },
          { name: "Starting Customers", description: "Total customers at the start of the period" },
        ]
      }}
      example={{
        inputs: [
          { label: "Customers Lost", value: "50" },
          { label: "Starting Customers", value: "1,000" },
        ],
        output: "5.00% Monthly Churn",
        interpretation: "5% monthly churn means ~46% annual attrition. Focus on retention to improve LTV."
      }}
      improvements={[
        "Implement proactive customer success programs",
        "Identify at-risk customers with health scoring",
        "Improve onboarding to drive early engagement",
        "Gather and act on customer feedback",
        "Create loyalty programs and retention incentives",
        "Analyze churn reasons to fix root causes",
      ]}
      relatedTools={relatedTools.map(t => ({ title: t.title, href: t.href, description: t.description }))}
      relatedService={{ title: "Email Marketing", href: "/email-marketing" }}
    >
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Calculate Churn Rate</CardTitle>
          <CardDescription>Enter your customer data</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="lostCustomers">Customers Lost This Period</Label>
            <Input
              id="lostCustomers"
              type="number"
              placeholder="50"
              value={lostCustomers}
              onChange={(e) => setLostCustomers(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="startCustomers">Customers at Period Start</Label>
            <Input
              id="startCustomers"
              type="number"
              placeholder="1000"
              value={startCustomers}
              onChange={(e) => setStartCustomers(e.target.value)}
            />
          </div>

          <div className="flex gap-3">
            <Button onClick={calculate} className="flex-1">
              Calculate Churn
            </Button>
            <Button variant="outline" onClick={reset}>
              Reset
            </Button>
          </div>

          {result !== null && (
            <div className="mt-6 p-6 bg-primary/5 rounded-lg border border-primary/20">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Churn Rate</p>
                <p className={`text-4xl font-bold ${getAssessment(result).color}`}>
                  {result.toFixed(2)}%
                </p>
                <p className={`text-sm mt-3 ${getAssessment(result).color}`}>
                  {getAssessment(result).label}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </ToolPageLayout>
  );
};

export default ChurnCalculator;
